import { Controller } from "@hotwired/stimulus"

// Live customer-based repricing on the order form.
//
// Picking a customer who has a pinned price list fills each line's unit price
// from that list, and changing a line's product refills just that line — using
// the same /orders/po_customer_prices endpoint (PriceResolver precedence:
// grade/variant override -> grade -> family default -> catalog tier) the
// PO-review flow already uses. Previously the order form did NONE of this: the
// customer's list only applied server-side, on create, for still-blank prices,
// so selecting a customer never updated the visible prices.
//
// Manual overrides win: a price the field already holds (a saved price on an
// edit form, or one the user typed) is NEVER clobbered. Only BLANK fields and
// values THIS controller auto-filled get updated — mirroring the server's
// apply_default_unit_price "manual override always wins" rule. Auto-filled
// fields carry data-autofilled; typing into a field clears that flag so a later
// customer switch won't overwrite the manual value.
export default class extends Controller {
  static targets = ["row", "price"]

  connect() {
    // Monotonic request generation. Every reprice bumps it and captures its
    // value; a response only applies if it's still the latest — so a slow reply
    // for a previously selected customer can't clobber the current prices.
    this._seq = 0
  }

  customerSelect() {
    return this.element.querySelector("[name='order[customer_id]']")
  }

  // The user edited a price → treat it as manual from now on.
  markManual(event) {
    delete event.target.dataset.autofilled
  }

  // Customer changed: reprice every line whose product the list prices.
  async customerChanged() {
    const customerId = this.customerSelect()?.value
    const rows = this.rowTargets
    const ids = []
    rows.forEach((row) => {
      const pid = this._productId(row)
      if (pid) ids.push(pid)
    })
    if (!customerId || ids.length === 0) return

    const seq = ++this._seq
    const prices = await this._fetchPrices(customerId, ids)
    // A newer reprice started while we awaited — its response is authoritative.
    if (!prices || seq !== this._seq) return

    rows.forEach((row) => {
      const pid = this._productId(row)
      const lp = pid ? prices[pid] : null
      if (lp != null) this._applyPrice(row, lp)
    })
  }

  // A line's product changed: refill just that line from the current customer.
  async productChanged(event) {
    const row = this._rowFor(event.target)
    const customerId = this.customerSelect()?.value
    const pid = event.target.value
    if (!row || !customerId || !pid) return

    const seq = ++this._seq
    const prices = await this._fetchPrices(customerId, [pid])
    if (seq !== this._seq) return
    const lp = prices ? prices[pid] : null
    if (lp != null) this._applyPrice(row, lp)
  }

  // ----- helpers -----

  _rowFor(el) {
    return el.closest("[data-order-pricing-target='row']") || el.closest(".nested-fields")
  }

  _productId(row) {
    const sel = row.querySelector("[name$='[product_id]']")
    return sel && sel.value ? sel.value : null
  }

  // Overwrite ONLY blank or previously auto-filled prices — never a manual
  // entry or a saved price the field already carries.
  _applyPrice(row, price) {
    const input = row.querySelector("[data-order-pricing-target='price']") || row.querySelector("[name$='[unit_price]']")
    if (!input) return
    const isAuto = input.dataset.autofilled === "true"
    if (input.value.trim() !== "" && !isAuto) return
    input.value = parseFloat(price).toFixed(2)
    input.dataset.autofilled = "true"
  }

  async _fetchPrices(customerId, ids) {
    const params = new URLSearchParams()
    params.set("customer_id", customerId)
    ids.forEach((id) => params.append("product_ids[]", id))
    try {
      const resp = await fetch(`/orders/po_customer_prices?${params.toString()}`, { headers: { Accept: "application/json" } })
      if (!resp.ok) return null
      const data = await resp.json()
      return (data && data.prices) || {}
    } catch (e) {
      return null
    }
  }
}
